/**
 * TimesheetPendingPanel
 * Shows timesheets awaiting approval, newest first
 * GET /api/admin/timesheets/?status[]=pending&page_size=30&ordering=-date
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ClipboardCheck, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const fmtDate = s => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
};

const initials = name =>
  (name || '').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() || '?';

const COLORS = ['#3B82F6','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316'];
const userColor = name =>
  COLORS[(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];

const TimesheetPendingPanel = () => {
  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    axios.get(`${API_BASE}/api/admin/timesheets/`, {
      headers,
      params: { status: 'pending', page_size: 100 },
    })
      .then(r => {
        const arr = r.data?.results ?? (Array.isArray(r.data) ? r.data : []);
        setItems(arr);
        setTotal(r.data?.pagination?.count ?? arr.length);
      })
      .catch(() => setError('Could not load pending timesheets.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">

      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <ClipboardCheck size={13} className="text-white" />
          </div>
          <span className="text-sm font-semibold text-slate-800">Pending Timesheets</span>
          {!loading && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              total > 0 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
            }`}>
              {total > 0 ? `${total} pending` : 'all clear'}
            </span>
          )}
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-300">
          <CheckCircle2 size={28} />
          <span className="text-xs">No pending timesheets</span>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">

          {/* column headers */}
          <div className="flex items-center gap-2 px-3 py-1 border-b border-steel-100 bg-steel-50 flex-shrink-0">
            <span className="flex-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Employee / Project</span>
            <span className="w-16 text-[10px] font-semibold text-slate-400 uppercase tracking-wide text-center">Date</span>
            <span className="w-14 text-[10px] font-semibold text-slate-400 uppercase tracking-wide text-right">Hours</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.map((item, i) => {
              const name  = item.user?.full_name || '—';
              const color = userColor(name);
              const proj  = item.projekt?.name || item.project?.name || '—';
              const rawPic = item.user?.profile_picture;
              const pic   = rawPic
                ? (rawPic.startsWith('http') ? rawPic : `${API_BASE}${rawPic}`)
                : null;

              return (
                <div key={item.id}
                  className={`flex items-center gap-2 px-3 py-2 border-b border-steel-50 hover:bg-steel-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>

                  {/* avatar */}
                  {pic ? (
                    <img src={pic} alt={name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                      style={{ backgroundColor: color, fontSize: 10, fontWeight: 700 }}>
                      {initials(name)}
                    </div>
                  )}

                  {/* name + project */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate leading-tight">{name}</p>
                    <p className="text-[11px] text-slate-400 truncate leading-tight">{proj}</p>
                  </div>

                  {/* date */}
                  <span className="w-16 text-[11px] text-slate-500 text-center flex-shrink-0">
                    {fmtDate(item.date)}
                  </span>

                  {/* hours — already formatted string e.g. "8h 30min" */}
                  <span className="w-14 text-[11px] font-semibold text-slate-600 text-right flex-shrink-0">
                    {item.worked_hours || '—'}
                  </span>
                </div>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
};

export default TimesheetPendingPanel;
